import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  MoreHorizontal,
  FileText,
  Loader2,
  Eye,
  Pencil,
  Trash2,
  Send,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  DollarSign,
  Copy,
  Download,
  CreditCard,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CounterpartyBadge } from '@/components/trading/sales/counterparty-badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CancellationReasonDialog } from '@/components/ui/cancellation-reason-dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { useSessionState } from '@/hooks/use-session-state';
import { usePermission } from '@/components/guards/permission-guard';

const statusConfig: Record<string, { color: string; icon: React.ElementType }> = {
  DRAFT: { color: 'bg-gray-100 text-gray-700', icon: Clock },
  SENT: { color: 'bg-blue-100 text-blue-700', icon: Send },
  VIEWED: { color: 'bg-purple-100 text-purple-700', icon: Eye },
  PARTIALLY_PAID: { color: 'bg-yellow-100 text-yellow-700', icon: DollarSign },
  PAID: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  OVERDUE: { color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  CANCELLED: { color: 'bg-gray-100 text-gray-500', icon: XCircle },
  WRITTEN_OFF: { color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
};

export function TradingInvoicesPage() {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const canEdit = usePermission('INVOICES', 'EDIT', 'TRADING');
  const [debouncedSearch, setDebouncedSearch] = useSessionState(
    'trading:invoices:debouncedSearch',
    ''
  );
  const [searchQuery, setSearchQuery] = useState(debouncedSearch);
  const [selectedStatus, setSelectedStatus] = useSessionState<string>(
    'trading:invoices:selectedStatus',
    'all'
  );
  const [page, setPage] = useState(1);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<string | null>(null);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  };

  const { data, isLoading, error } = trpc.tradingInvoice.list.useQuery({
    search: debouncedSearch || undefined,
    status: selectedStatus !== 'all' ? (selectedStatus as any) : undefined,
    page,
  });

  const utils = trpc.useUtils();

  const cancelMutation = trpc.tradingInvoice.cancel.useMutation({
    onSuccess: () => {
      utils.tradingInvoice.list.invalidate();
      showSuccessToast(t('finance.invoices.cancelled'));
      setDeleteConfirmOpen(false);
      setInvoiceToDelete(null);
    },
    onError: (error: { message: string }) => {
      showErrorToast(error.message);
    },
  });

  const sendMutation = trpc.tradingInvoice.send.useMutation({
    onSuccess: () => {
      utils.tradingInvoice.list.invalidate();
      showSuccessToast(t('finance.invoices.sent'));
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const duplicateMutation = trpc.tradingInvoice.duplicate.useMutation({
    onSuccess: (data) => {
      utils.tradingInvoice.list.invalidate();
      showSuccessToast(t('finance.invoices.duplicated'));
      navigate(`/trading/finance/invoices/${data.id}`);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const invoices = data?.invoices ?? [];
  const pagination = data?.pagination;

  const handleDeleteInvoice = (id: string) => {
    setInvoiceToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = (reason: string) => {
    if (invoiceToDelete) {
      cancelMutation.mutate({ id: invoiceToDelete, reason: reason || 'Cancelled by user' });
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return t('finance.invoices.statusDraft');
      case 'SENT':
        return t('finance.invoices.statusSent');
      case 'VIEWED':
        return t('finance.invoices.statusViewed');
      case 'PARTIALLY_PAID':
        return t('finance.invoices.statusPartiallyPaid');
      case 'PAID':
        return t('finance.invoices.statusPaid');
      case 'OVERDUE':
        return t('finance.invoices.statusOverdue');
      case 'CANCELLED':
        return t('finance.invoices.statusCancelled');
      case 'WRITTEN_OFF':
        return t('finance.invoices.statusWrittenOff');
      default:
        return status;
    }
  };

  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(value);
  };

  const formatDate = (dateValue: string | Date) => {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const statuses = [
    'DRAFT',
    'SENT',
    'VIEWED',
    'PARTIALLY_PAID',
    'PAID',
    'OVERDUE',
    'CANCELLED',
    'WRITTEN_OFF',
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('finance.invoices.title')}</h1>
          <p className="text-muted-foreground">{t('finance.invoices.subtitle')}</p>
        </div>
        <Button onClick={() => navigate('/trading/finance/invoices/new')}>
          <Plus className="mr-2 h-4 w-4" />
          {t('finance.invoices.addInvoice')}
        </Button>
      </div>

      {/* Cancel Confirmation Dialog */}
      <CancellationReasonDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={t('finance.invoices.cancelTitle')}
        description={t('finance.invoices.cancelDescription')}
        reasonLabel={t('finance.invoices.cancellationReason')}
        reasonPlaceholder={t('finance.invoices.cancellationReasonPlaceholder')}
        confirmLabel={t('finance.invoices.confirmCancel')}
        cancelLabel={tCommon('cancel')}
        onConfirm={confirmDelete}
        isLoading={cancelMutation.isPending}
      />

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <input
            type="text"
            placeholder={t('finance.invoices.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 w-full border pl-10 pr-4 text-sm focus:outline-none focus:ring-1"
          />
        </div>
        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="border-border focus:border-foreground focus:ring-foreground/20 h-8 w-[180px] border focus:ring-1">
            <SelectValue placeholder={t('finance.invoices.selectStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('finance.invoices.allStatuses')}</SelectItem>
            {statuses.map((status) => (
              <SelectItem key={status} value={status}>
                {getStatusLabel(status)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <span className="text-muted-foreground ml-2">{t('common.loading')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('common.error')}</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && invoices.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <FileText className="text-muted-foreground/50 h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">{t('finance.invoices.noInvoices')}</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {t('finance.invoices.noInvoicesDesc')}
          </p>
          <Button className="mt-4" onClick={() => navigate('/trading/finance/invoices/new')}>
            <Plus className="mr-2 h-4 w-4" />
            {t('finance.invoices.addInvoice')}
          </Button>
        </div>
      )}

      {/* Invoices Table */}
      {!isLoading && !error && invoices.length > 0 && (
        <>
          <div className="border-border bg-card overflow-hidden border">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('finance.invoices.invoiceNumber')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('finance.invoices.customer')}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium">
                    {t('finance.invoices.status')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('finance.invoices.issueDate')}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    {t('finance.invoices.dueDate')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('finance.invoices.total')}
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium">
                    {t('finance.invoices.balance')}
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium">
                    {tCommon('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {invoices.map((invoice) => {
                  const config = statusConfig[invoice.status] || statusConfig.DRAFT;
                  const StatusIcon = config?.icon ?? Clock;
                  const balance = Number(invoice.total) - Number(invoice.paidAmount);

                  return (
                    <tr key={invoice.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <Link
                          to={`/trading/finance/invoices/${invoice.id}`}
                          className="text-primary font-mono text-sm font-medium hover:underline"
                        >
                          {invoice.invoiceNumber}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">
                              {(invoice as any).counterpartyName || invoice.customerName}
                            </span>
                            <CounterpartyBadge kind={(invoice as any).counterpartyKind} />
                          </div>
                          <div className="text-muted-foreground text-xs">{invoice.email}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${config?.color ?? 'bg-gray-100 text-gray-700'}`}
                        >
                          <StatusIcon className="h-3 w-3" />
                          {getStatusLabel(invoice.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">{formatDate(invoice.invoiceDate)}</td>
                      <td className="px-4 py-3 text-sm">
                        {invoice.dueDate ? formatDate(invoice.dueDate) : '-'}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium">
                        {formatCurrency(Number(invoice.total), invoice.currency)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium">
                        <span className={balance > 0 ? 'text-red-600' : 'text-green-600'}>
                          {formatCurrency(balance, invoice.currency)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => navigate(`/trading/finance/invoices/${invoice.id}`)}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              {tCommon('view')}
                            </DropdownMenuItem>
                            {canEdit && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    navigate(`/trading/finance/invoices/${invoice.id}/edit`)
                                  }
                                >
                                  <Pencil className="mr-2 h-4 w-4" />
                                  {tCommon('edit')}
                                </DropdownMenuItem>
                            )}
                            {invoice.status === 'DRAFT' && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => sendMutation.mutate({ id: invoice.id })}
                                >
                                  <Send className="mr-2 h-4 w-4" />
                                  {t('finance.invoices.send')}
                                </DropdownMenuItem>
                              </>
                            )}
                            {['SENT', 'VIEWED', 'PARTIALLY_PAID', 'OVERDUE'].includes(
                              invoice.status
                            ) && (
                              <DropdownMenuItem
                                onClick={() =>
                                  navigate(`/trading/finance/payments/new?invoiceId=${invoice.id}`)
                                }
                              >
                                <CreditCard className="mr-2 h-4 w-4" />
                                {t('finance.invoices.recordPayment')}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() =>
                                window.open(`/api/trading-invoice/${invoice.id}/pdf`, '_blank')
                              }
                            >
                              <Download className="mr-2 h-4 w-4" />
                              {t('finance.invoices.downloadPdf')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => duplicateMutation.mutate(invoice.id)}>
                              <Copy className="mr-2 h-4 w-4" />
                              {t('finance.invoices.duplicate')}
                            </DropdownMenuItem>
                            {invoice.status === 'DRAFT' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={() => handleDeleteInvoice(invoice.id)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  {tCommon('delete')}
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-sm">
                {tCommon('showing')} {(pagination.page - 1) * pagination.limit + 1} -{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} {tCommon('of')}{' '}
                {pagination.total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="border-border hover:bg-muted focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1 disabled:opacity-50"
                >
                  {tCommon('previous')}
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === pagination.totalPages}
                  className="border-border hover:bg-muted focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1 disabled:opacity-50"
                >
                  {tCommon('next')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
